import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpHeaders,
} from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

import { AuthRepo } from '../repos/auth.repo';
import { OperationRepo } from '../repos/operation.repo';
import { environment } from '../../../../environments/environment';

export class Filter {
  constructor(
    public variable: string,
    public condition: string,
    public value: string,
    public label?: string,
    public name?: string
  ) {}

  public toQueryString(): string {
    return this.variable + '|' + this.condition + '|' + this.value;
  }
}

export interface Pagination {
  total?: number;
  per_page?: number;
  current_page?: number;
  from?: number;
  to?: number;
  last_page?: number;
}

@Injectable()
export abstract class Resource {
  protected url = environment.SERVICE_URL;

  protected clientId;

  protected queryFilters: string = '';
  protected paginationInfo: Pagination = {};

  protected params: HttpParams = new HttpParams();
  protected me = this;

  protected filters: Filter[] = [];
  protected unpaginated: boolean = false;

  constructor(
    protected http: HttpClient,
    protected authRepo: AuthRepo,
    protected opRepo: OperationRepo
  ) {}

  allWithParams(paramsData, page?: number) {
    const headers = this.getHeaders();
    const filters = this.applyQueryFilters();

    let params = page ? this.params.append('page', page + '') : this.params;
    params = filters ? params.append('filters', filters) : params;

    Object.entries(paramsData).forEach(([key, value]) => {
      params = params.append(key, value.toString());
    });

    const options = { headers: headers, params: params };

    return this.http.get(this.url + this.getEntity(), options).pipe(
      map(res => this.mapArray(res)),
      catchError(err => this.handleError(err))
    );
  }

  all(page?: number) {
    const headers = this.getHeaders(this.clientId);
    const filters = this.applyQueryFilters();

    let params = page ? this.params.append('page', page + '') : this.params;
    params = filters ? params.append('filters', filters) : params;
    params = this.unpaginated ? params.append('unpaginated', '') : params;
    const options = { headers: headers, params: params };

    return this.http.get(this.url + this.getEntity(), options).pipe(
      map(res => this.mapArray(res)),
      catchError(err => this.handleError(err))
    );
  }

  get(id) {
    const headers = this.getHeaders();
    const options = { headers: headers };
    return this.http.get(this.url + this.getEntity() + '/' + id, options).pipe(
      map((res: unknown) => this.map(res['data'])),
      catchError(err => this.handleError(err))
    );
  }

  getInfo() {
    const headers = this.getHeaders();
    const options = { headers: headers };
    return this.http.get(this.url + this.getEntity(), options).pipe(
      map((res: unknown) => this.map(res['data'])),
      catchError(err => this.handleError(err))
    );
  }

  store(data) {
    const headers = this.getHeaders();
    const options = { headers: headers };
    return this.http.post(this.url + this.getEntity(), data, options).pipe(
      map((res: unknown) => {
        return this.map(res['data']);
      }),
      catchError(err => this.handleError(err))
    );
  }

  update(id, data) {
    const headers = this.getHeaders();
    const options = { headers: headers };
    const complement = id ? '/' + id : '';

    return this.http
      .put(this.url + this.getEntity() + complement, data, options)
      .pipe(
        map((res: unknown) => {
          return this.map(res['data']);
        }),
        catchError(err => this.handleError(err))
      );
  }

  delete(id) {
    const headers = this.getHeaders();
    const options = { headers: headers };
    return this.http
      .delete(this.url + this.getEntity() + '/' + id, options)
      .pipe(
        map((res: unknown) => {
          return res['data'];
        }),
        catchError(err => this.handleError(err))
      );
  }

  getPaginationInfo() {
    return this.paginationInfo;
  }

  unpaginate(unpaginated = true) {
    this.unpaginated = unpaginated;
  }

  getHeaders(clientId?) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + this.authRepo.getUser().token,
      'Operation-Id': this.opRepo.getOperation().id + '',
    });

    if (clientId) {
      headers = headers.append('Client-Id', clientId);
    }

    return headers;
  }

  addFilter(
    variable: string,
    condition: string,
    value: string,
    label?: string,
    name?: string
  ) {
    if (label) {
      console.log(label);
    }

    if (name) {
      console.log(name);
    }
    this.filters.push(new Filter(variable, condition, value));
  }

  removeFilter(filter: Filter) {
    this.filters.splice(this.filters.indexOf(filter), 1);
  }

  clearFilters() {
    this.filters = [];
  }

  getFilterByVariable(variable) {
    return this.filters.find(filter => filter.variable == variable);
  }

  handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    }

    return throwError(error);
  }

  protected applyQueryFilters() {
    let filters = null;
    const queryFilters: string[] = [];

    for (const filter of this.filters) {
      queryFilters.push(filter.toQueryString());
    }

    if (queryFilters.length > 0) {
      filters = queryFilters.join(';');
    }
    return filters;
  }

  private mapArray(response) {
    const data = response.data;
    if (typeof data.total != 'undefined') {
      return this.mapPaginatedArray(data);
    }

    const array = [];
    for (const item of data) {
      const i = this.map(item);
      array.push(i);
    }

    return array;
  }

  private mapPaginatedArray(data) {
    this.paginationInfo = {
      total: data.total,
      per_page: data.per_page,
      current_page: data.current_page,
      from: data.from,
      to: data.to,
      last_page: data.last_page,
    };

    data = data.data;
    const array: unknown[] = [];
    for (const item of data) {
      const i = this.map(item);
      array.push(i);
    }
    return array;
  }
  protected abstract getEntity();
  protected abstract map(item: unknown);

  updateSocketTest() {
    const headers = this.getHeaders();
    const options = { headers: headers };
    return this.http
      .get('https://enterprises-bt.paso.com.co/' + 'positionUpdate', options)
      .pipe(
        map((res: unknown) => res['data']),
        catchError(err => this.handleError(err))
      );
  }

  createSocketTest() {
    const headers = this.getHeaders();
    const options = { headers: headers };
    return this.http
      .get('https://enterprises-bt.paso.com.co/' + 'positionCreate', options)
      .pipe(
        map((res: unknown) => res['data']),
        catchError(err => this.handleError(err))
      );
  }

  deleteSocketTest() {
    const headers = this.getHeaders();
    const options = { headers: headers };
    return this.http
      .get('https://enterprises-bt.paso.com.co/' + 'positionDelete', options)
      .pipe(
        map((res: unknown) => res['data']),
        catchError(err => this.handleError(err))
      );
  }

  randomSocketTest() {
    const headers = this.getHeaders();
    const options = { headers: headers };
    return this.http
      .get('https://enterprises-bt.paso.com.co/' + 'positionRandom', options)
      .pipe(
        map((res: unknown) => res['data']),
        catchError(err => this.handleError(err))
      );
  }
}
